<template>
  <div
    class="modal fade rfo-autocomplete-modal rfo-unified-modal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="rydUserAutocompleteModalLabel"
    :class="{ 'show d-flex ryd-modal-backdrop': show }"
    :style="show ? 'display: flex;' : 'display: none;'"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered ryd-modal-dialog" role="document">
      <div class="modal-content fpc-dialog">
        <div class="fpc-dialog__header">
          <div class="d-flex align-items-center gap-3">
            <div class="fpc-dialog__header-icon">
              <i class="fa fa-user" aria-hidden="true"></i>
            </div>
            <div>
              <h5 class="modal-title fpc-dialog__title" id="rydUserAutocompleteModalLabel">
                {{ title }}
              </h5>
              <div class="fpc-dialog__subtitle">Search by first name, last name, or email</div>
            </div>
          </div>
          <button
            type="button"
            class="fpc-dialog__close-btn"
            aria-label="Close"
            @click="$emit('close')"
          >
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <div class="fpc-dialog__search-bar d-flex align-items-center gap-3 flex-wrap">
          <div class="fpc-dialog__search-wrap">
            <i class="fa fa-search fpc-dialog__search-icon" aria-hidden="true"></i>
            <input
              ref="searchInput"
              v-model="searchTerm"
              type="text"
              class="fpc-dialog__search-input"
              placeholder="Type to filter…"
              autocomplete="off"
            />
          </div>
          <button
            type="button"
            class="fpc-dialog__fetch-btn btn btn-outline-secondary"
            :disabled="isLoading"
            @click="clearSearch"
          >
            <i class="fa fa-list me-1" aria-hidden="true"></i>
            Show All
          </button>
        </div>

        <div class="fpc-dialog__banner d-flex align-items-start gap-2">
          <i class="fa fa-info-circle fpc-dialog__banner-icon" aria-hidden="true"></i>
          <span>
            Only ryd internal users (<code>userGroup === 'ryd'</code>) are shown. Selection sends
            the user's <strong>uid</strong> as the filter value.
          </span>
        </div>

        <div class="fpc-dialog__body">
          <div v-if="isLoading" class="fpc-dialog__state d-flex flex-column align-items-center">
            <div class="spinner-border text-primary fpc-dialog__spinner" role="status">
              <span class="visually-hidden">Loading…</span>
            </div>
            <p class="fpc-dialog__state-text">Loading ryd users…</p>
          </div>

          <div
            v-else-if="loadError"
            class="fpc-dialog__state d-flex flex-column align-items-center"
          >
            <div class="fpc-dialog__state-icon fpc-dialog__state-icon--warn">
              <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            </div>
            <h6 class="fpc-dialog__state-title">Could not load users</h6>
            <p class="fpc-dialog__state-body">{{ loadError }}</p>
            <button class="btn btn-sm btn-outline-primary mt-2" @click="loadUsers">
              <i class="fa fa-refresh me-1" />Retry
            </button>
          </div>

          <div
            v-else-if="visibleUsers.length === 0"
            class="fpc-dialog__state d-flex flex-column align-items-center"
          >
            <div class="fpc-dialog__state-icon fpc-dialog__state-icon--warn">
              <i class="fa fa-search" aria-hidden="true"></i>
            </div>
            <h6 class="fpc-dialog__state-title">No ryd users match this search</h6>
            <p class="fpc-dialog__state-body">Try a different name or email.</p>
          </div>

          <div v-else>
            <div class="fpc-dialog__results-meta d-flex align-items-center gap-2 flex-wrap">
              <span class="badge fpc-dialog__count-badge">
                {{ filteredUsers.length }} user<span v-if="filteredUsers.length !== 1">s</span>
              </span>
              <span v-if="totalPages > 1" class="fpc-dialog__page-hint">
                Page {{ page }} of {{ totalPages }}
              </span>
            </div>

            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0 fpc-dialog__table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>UID</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="user in visibleUsers"
                    :key="user.uid"
                    class="fpc-dialog__result-row"
                    @click="$emit('select', user)"
                  >
                    <td class="fw-semibold small">{{ displayName(user) }}</td>
                    <td class="small text-muted">{{ user.email || '—' }}</td>
                    <td>
                      <code class="fpc-dialog__code fpc-dialog__code--muted">{{ user.uid }}</code>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="totalPages > 1" class="fpc-dialog__pagination">
              <PaginationFleet
                :current-page="page"
                :total-pages="totalPages"
                :is-loading="false"
                @page-change="onPageChange"
              />
            </div>
          </div>
        </div>

        <div class="fpc-dialog__footer">
          <div class="fpc-dialog__footer-left">
            <span v-if="visibleUsers.length > 0" class="fpc-dialog__footer-hint">
              <i class="fa fa-mouse-pointer me-1" aria-hidden="true"></i>
              Click a row to select the user
            </span>
          </div>
          <button
            type="button"
            class="btn btn-outline-secondary fpc-dialog__cancel-btn"
            @click="$emit('close')"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { apiClient, API_PATHS } from '@/services/api';
import PaginationFleet from '@/components/common/PaginationFleet.vue';

interface RydUser {
  uid: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

const props = defineProps<{ show: boolean; title?: string }>();
defineEmits<{ (e: 'select', user: RydUser): void; (e: 'close'): void }>();

const PAGE_SIZE = 50;

const title = computed(() => props.title || 'Select ryd User');

const users = ref<RydUser[]>([]);
const isLoading = ref(false);
const loadError = ref('');
const searchTerm = ref('');
const page = ref(1);
const searchInput = ref<HTMLInputElement | null>(null);

const filteredUsers = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return users.value;
  return users.value.filter((u) => {
    const full = `${u.firstName || ''} ${u.lastName || ''}`.trim().toLowerCase();
    return full.includes(term) || (u.email || '').toLowerCase().includes(term);
  });
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredUsers.value.length / PAGE_SIZE)));

const visibleUsers = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE;
  return filteredUsers.value.slice(start, start + PAGE_SIZE);
});

function displayName(u: RydUser): string {
  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return name || u.email || u.uid;
}

function onPageChange(p: number) {
  page.value = Math.min(Math.max(1, p), totalPages.value);
}

function clearSearch() {
  searchTerm.value = '';
  page.value = 1;
}

async function loadUsers() {
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await apiClient.rfp.get(API_PATHS.rfp.users.ryd.all);
    users.value = data?.items || [];
  } catch (err: any) {
    loadError.value = err?.response?.data?.message || err?.message || 'Failed to load users.';
  } finally {
    isLoading.value = false;
  }
}

watch(searchTerm, () => {
  page.value = 1;
});

watch(
  () => props.show,
  (isOpen) => {
    if (!isOpen) return;
    if (users.value.length === 0 && !isLoading.value) loadUsers();
    nextTick(() => searchInput.value?.focus());
  },
);
</script>
